import { useMutation } from '@tanstack/react-query';
import { updateCustomerHomeAddress } from '@Pages/happyFlowPages/homeAddress/api/homeAddress.api.ts';
import { useBoundStore } from '@Store/useBoundStore';
import { OnboardingFormFields } from '@Types/index';
import { OnboardingFlowIdentifier } from '@Types/onboarding.types';
import { useSaveOwner } from '@Hooks/api/useSaveOwner';
import { getBusinessOwnerData } from '@Pages/happyFlowPages/ownersCenter/api/util';
import { UBORoles } from '@Store/index';
import type { AddressData, UboResponse } from '@Store/index';
import type { SaveOwnerBody } from '@Types/owner';
import type { IHomeAddressForm } from '@Types/homeAddress';

export const useUpdateCustomerHomeAddress = (onSuccess: () => void) => {
  const {
    onboardingFlowIdentifier,
    authorizedUserId,
    customerId: authUserCustomerId,
  } = useBoundStore.getState().onboardingSlice.getOnboardingData();
  const isAuthUserFlow = onboardingFlowIdentifier === OnboardingFlowIdentifier.AUTHORIZED_USER;

  const {
    customerData: {
      businessAddressData: { sameAsPrimary: isBussinessAddressSameAsPrimary },
      customerBusinessData: { businessUbos, externalId: businessId },
    },
  } = useBoundStore((state) => state.customer);

  const { customerId, businessExternalId, isEmbedded } = getBusinessOwnerData();
  const { mutateAsync: asyncUpdateUbo } = useSaveOwner({
    customerId: customerId!,
    businessExternalId: businessExternalId!,
    isEmbedded,
    onSuccess: async (data, input) => {
      const { getCustomerData, setCustomerData } = useBoundStore.getState();
      const customerToUpdate = { ...getCustomerData() };
      customerToUpdate.customerData.customerBusinessData.businessUbos!.uboResponseList! =
        customerToUpdate.customerData.customerBusinessData.businessUbos!.uboResponseList!.map(
          (ubo) =>
            ubo.externalId !== input.externalId
              ? ubo
              : mapSaveOwnerBodyToUbo({ ...input, allowEditing: data.allowEditing })
        );
      setCustomerData(customerToUpdate);
    },
  });

  const homeAddressCtx = {
    customerId: customerId,
    isEmbedded,
    authorizedUserId,
    authUserCustomerId,
    isAuthUser: isAuthUserFlow,
  };

  return useMutation({
    mutationFn: (props: IHomeAddressForm) => updateCustomerHomeAddress(props, homeAddressCtx),
    mutationKey: ['updateCustomerHomeAddress'],
    onSuccess: async (_, input) => {
      if (isAuthUserFlow) {
        onSuccess();
        return;
      }

      if (isBussinessAddressSameAsPrimary) {
        const { getCustomerData, setCustomerData } = useBoundStore.getState();
        const currentCustomer = getCustomerData();
        const businessAddress = currentCustomer.customerData.businessAddressData;

        const isAddressDifferent =
          businessAddress.line1 !== input[OnboardingFormFields.LINE1] ||
          businessAddress.line2 !== input[OnboardingFormFields.APARTMENT] ||
          businessAddress.city !== input[OnboardingFormFields.CITY] ||
          businessAddress.state !== input[OnboardingFormFields.STATE]?.value ||
          businessAddress.postalCode !== input[OnboardingFormFields.ZIP] ||
          businessAddress.country !== input[OnboardingFormFields.COUNTRY]?.code;

        if (isAddressDifferent) {
          const customerToUpdate = { ...currentCustomer };
          customerToUpdate.customerData.businessAddressData.sameAsPrimary = false;
          setCustomerData(customerToUpdate);
        }
      }

      if (businessUbos?.uboResponseList?.length && businessId) {
        const sameAsPrimaryUbos = businessUbos.uboResponseList.filter(
          ({ customerAddressData: { sameAsPrimary } }) => sameAsPrimary
        );
        await Promise.allSettled(
          sameAsPrimaryUbos.map(async (ubo) =>
            asyncUpdateUbo(
              mapUboToSaveOwnerBody({
                ...ubo,
                customerAddressData: {
                  ...mapHomeAddressFormInputToApiAddress(input),
                  sameAsPrimary: true,
                },
              })
            )
          )
        );
      }
      onSuccess();
    },
  });
};

const mapUboToSaveOwnerBody = (ubo: UboResponse) => {
  return {
    externalId: ubo.externalId,
    email: ubo.email,
    role: ubo.role,
    phoneNumber: ubo.phoneNumber,
    phoneNumberCountryCode: ubo?.phoneNumberCountryCode || 'US',
    ownershipPercent: ubo.ownershipPercent,
    customerSSN: {
      ssn: ubo.ssn,
      birthDate: ubo.birthDate,
      idExpirationDate: ubo?.customerSSNData?.idExpirationDate,
      idGovNumber: ubo?.customerSSNData?.idGovNumber,
      idType: ubo?.customerSSNData?.idType,
      idNumber: ubo?.customerSSNData?.idNumber,
      idCountry: ubo?.customerSSNData?.idCountry,
    },
    customerAddress: {
      ...ubo.customerAddressData,
    },
    customerPersonalInfo: {
      firstName: ubo.firstName,
      lastName: ubo.lastName,
    },
  } satisfies SaveOwnerBody;
};

const mapSaveOwnerBodyToUbo = (saveOwnerBody: SaveOwnerBody & { allowEditing: string }) => {
  return {
    externalId: saveOwnerBody.externalId || '',
    email: saveOwnerBody.email,
    role: saveOwnerBody.role,
    phoneNumber: saveOwnerBody.phoneNumber,
    ownershipPercent: saveOwnerBody.ownershipPercent,
    customerSSNData: {
      idCountry: saveOwnerBody.customerSSN.idCountry,
      idExpirationDate: saveOwnerBody.customerSSN.idExpirationDate,
      idGovNumber: saveOwnerBody.customerSSN.idGovNumber,
      idNumber: saveOwnerBody.customerSSN.idNumber,
      idType: saveOwnerBody.customerSSN.idType,
    },

    ssn: saveOwnerBody.customerSSN.ssn !== '' ? saveOwnerBody.customerSSN.ssn : undefined,
    birthDate: saveOwnerBody.customerSSN.birthDate,
    customerAddressData: {
      city: saveOwnerBody.customerAddress.city,
      line1: saveOwnerBody.customerAddress.line1,
      line2: saveOwnerBody.customerAddress.line2,
      state: saveOwnerBody.customerAddress.state,
      postalCode: saveOwnerBody.customerAddress.postalCode,
      sameAsPrimary: saveOwnerBody.customerAddress.sameAsPrimary,
    },
    firstName: saveOwnerBody.customerPersonalInfo.firstName,
    lastName: saveOwnerBody.customerPersonalInfo.lastName,
    allowEditing: saveOwnerBody.role === UBORoles.PARTNER_FULL_ACCESS,
  } satisfies UboResponse;
};

const mapHomeAddressFormInputToApiAddress = (homeAddress: IHomeAddressForm) => {
  return {
    city: homeAddress[OnboardingFormFields.CITY],
    line1: homeAddress[OnboardingFormFields.LINE1],
    line2: homeAddress[OnboardingFormFields.APARTMENT],
    postalCode: homeAddress[OnboardingFormFields.ZIP],
    state: homeAddress[OnboardingFormFields.STATE]?.value,
    country: homeAddress[OnboardingFormFields.COUNTRY]?.code,
  } satisfies Omit<AddressData, 'sameAsPrimary'>;
};
